/**
 * Region display names and flags for the arbitrage page.
 *
 * The keys are what the backend writes into `arbitrage_routes` after
 * ArbitrageRouteService::normaliseRegion() — `north-america` is already folded
 * into `usa` there, so it never reaches this map. Values outside the list are
 * title-cased from their own value rather than relabelled.
 *
 * Every region gets a flag (Alex, 2026-10-12). A continent is shown by the
 * market that dominates its quotes: Asia is the Hong Kong trade groups,
 * Middle East is Dubai. The names stay the region's own, so the flag reads as
 * "where the price comes from", not a claim that the region is one country.
 */
const REGION_LABELS = {
  uk: "UK",
  europe: "Europe",
  usa: "US",
  "middle-east": "Middle East",
  asia: "Asia",
  "south-america": "South America",
  africa: "Africa",
  australia: "Australia",
  unknown: "Unstated",
};

const REGION_FLAGS = {
  uk: "GB",
  europe: "EU",
  usa: "US",
  asia: "HK",
  "middle-east": "AE",
  australia: "AU",
  africa: "ZA",
  "south-america": "BR",
};

export const regionLabel = (region) =>
  REGION_LABELS[region] ||
  String(region || "")
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

/** ISO code for react-country-flag; null only for a region we have never seen. */
export const regionFlag = (region) => REGION_FLAGS[region] || null;

/**
 * Where each region sits on the globe, [lon, lat].
 *
 * The same market each flag names — Asia at Hong Kong, Middle East at Dubai —
 * so the map and the flags agree. Europe is a continent with no single hub, so
 * it sits near its geographic middle (southern Germany) rather than on a city
 * that would claim to be the whole market.
 */
const REGION_POINTS = {
  uk: [-1.5, 52.5],
  europe: [10.5, 48.5],
  usa: [-98.8, 39.9],
  asia: [114.17, 22.32],
  "middle-east": [55.27, 25.2],
  australia: [134.2, -25.6],
  africa: [25.2, -29.0],
  "south-america": [-53.2, -10.7],
};

export const regionPoint = (region) => REGION_POINTS[region] || null;

/** Mirrors the backend's ArbitrageRouteService::MIN_GAP_PERCENT. */
export const MIN_GAP_PERCENT = 5;

/**
 * Every profitable pair among one reference's legs — the same rule the
 * nightly job applies (ArbitrageRouteService::routesFor), run in the browser
 * on the legs the card already carries, so the globe opens with no request.
 *
 * Percent is of the CHEAPER side; score is the supply-pressure reading
 * (cheap leg's share of quotes × the dear leg's scarcity × gap), counted over
 * the pools the pair spans.
 */
export const routesFromLegs = (legs = []) => {
  const quotesByPool = {};
  legs.forEach((leg) => {
    quotesByPool[leg.pool] = (quotesByPool[leg.pool] || 0) + leg.quotes;
  });
  const allQuotes = Object.values(quotesByPool).reduce((a, b) => a + b, 0);

  const routes = [];
  legs.forEach((buy) => {
    if (buy.median <= 0) return;
    legs.forEach((sell) => {
      if (sell.median <= buy.median) return;
      const gap = sell.median - buy.median;
      const gapPercent = Math.round((gap / buy.median) * 1000) / 10;
      if (gapPercent < MIN_GAP_PERCENT) return;

      const total = buy.pool === sell.pool ? quotesByPool[buy.pool] : allQuotes;
      const score = (buy.quotes / total) * (1 - sell.quotes / total) * gapPercent;

      routes.push({
        buy,
        sell,
        gap,
        gapPercent,
        score: Math.round(score * 100) / 100,
        type: buy.pool === sell.pool ? buy.pool : "cross",
      });
    });
  });
  return routes;
};

export default regionLabel;

/** Stable id for a route between two legs: "trade|asia>market|uk". */
export const routeKey = (route) =>
  `${route.buy.pool}|${route.buy.region}>${route.sell.pool}|${route.sell.region}`;
